import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, ShieldCheck } from "lucide-react";
import { listUsers, setUserRole } from "@/lib/admin.functions";

const ROLES = ["super_admin", "admin", "moderator"] as const;

export const Route = createFileRoute("/_authenticated/admin/users")({
  component: UsersPage,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="py-20 text-center">
        <p className="text-red-400 mb-3">{error.message}</p>
        <button onClick={() => { router.invalidate(); reset(); }} className="bg-white text-zinc-950 px-5 py-2 rounded-full text-sm font-semibold">Try again</button>
      </div>
    );
  },
  notFoundComponent: () => <div className="text-zinc-400">Not found.</div>,
});

function UsersPage() {
  const listFn = useServerFn(listUsers);
  const setFn = useServerFn(setUserRole);
  const qc = useQueryClient();
  const { data: users = [], isLoading } = useQuery({ queryKey: ["admin-users"], queryFn: () => listFn() });

  const toggle = useMutation({
    mutationFn: (vars: { user_id: string; role: (typeof ROLES)[number]; action: "grant" | "revoke" }) => setFn({ data: vars }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-users"] }),
  });

  return (
    <div className="space-y-6 max-w-5xl">
      <header>
        <p className="text-amber-300 text-xs font-semibold tracking-widest uppercase">Access</p>
        <h1 className="font-serif text-3xl text-white mt-1">Users & Roles</h1>
        <p className="text-xs text-zinc-500 mt-2 max-w-xl">Only super admins can grant the super_admin role. The system refuses to remove the last super admin.</p>
      </header>

      {toggle.error && <p className="text-red-400 text-sm">{(toggle.error as Error).message}</p>}

      {isLoading ? (
        <div className="py-20 grid place-items-center"><Loader2 className="size-5 animate-spin text-zinc-500" /></div>
      ) : (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.02] text-zinc-500 text-xs uppercase tracking-wider">
              <tr><th className="text-left px-4 py-3">User</th><th className="text-left px-4 py-3">Last sign in</th><th className="text-left px-4 py-3">Roles</th></tr>
            </thead>
            <tbody>
              {users.map((u: any) => (
                <tr key={u.id} className="border-t border-white/5">
                  <td className="px-4 py-3">
                    <p className="text-white">{u.email || "—"}</p>
                    <p className="text-xs text-zinc-400">{u.id.slice(0, 8)}…</p>
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-400">{u.last_sign_in_at ? new Date(u.last_sign_in_at).toLocaleString() : "never"}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1.5">
                      {ROLES.map((r) => {
                        const has = u.roles.includes(r);
                        return (
                          <button
                            key={r}
                            disabled={toggle.isPending}
                            onClick={() => toggle.mutate({ user_id: u.id, role: r, action: has ? "revoke" : "grant" })}
                            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${has ? "bg-amber-300/15 text-amber-200 border-amber-300/40" : "bg-white/5 text-zinc-400 border-white/10 hover:border-amber-300/30"}`}
                          >
                            {has && <ShieldCheck className="inline size-3 mr-1" />}
                            {r}
                          </button>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}